import {
  AdminFetchRetryConfig,
  AdminFetchRetryConfigs,
  AdminRescheduleRetryJob,
  AdminUpdateRetryConfig,
} from 'src/constants/actionTypes'
import type {
  FetchRetryConfigPayload,
  FetchRetryConfigsPayload,
  RescheduleRetryJobPayload,
  RetryConfigDetail,
  RetryConfigsPage,
} from 'src/interfaces/retryConfig'
import { resolveErrorMessage } from 'src/utils/sagaErrorMessage'
import { Action } from 'src/utils/reduxUtils'
import type { RetryConfigState } from './types'

const getEmptyDetail = (): RetryConfigDetail => {
  return {
    config: null,
    actionJobs: [],
    payment: null,
  }
}

const getInitialState = (): RetryConfigState => {
  return {
    filters: { partnerId: null, partnerMarketId: null, onlyScheduled: false },
    sortDirection: 'ASC',
    configs: [],
    pagination: {},
    loadedPartnerId: null,
    listLoading: false,
    listError: null,
    detailConfigId: null,
    detail: getEmptyDetail(),
    detailLoading: false,
    detailError: null,
    saving: false,
    reschedulingJobId: null,
  }
}

export const retryConfigReducer = (
  state: RetryConfigState = getInitialState(),
  action: Action<unknown>,
): RetryConfigState => {
  switch (action.type) {
    case AdminFetchRetryConfigs.type.REQUEST: {
      const payload = action.payload as FetchRetryConfigsPayload | undefined
      const partnerId = payload?.partnerId ?? null
      const isSamePartner = partnerId === state.filters.partnerId

      return {
        ...state,
        filters: {
          partnerId,
          partnerMarketId: payload?.partnerMarketId ?? null,
          onlyScheduled: payload?.onlyScheduled ?? false,
        },
        sortDirection: payload?.sortDirection ?? state.sortDirection,
        // Same partner: keep the rows and pagination by reference so a refresh or a
        // post-reschedule refetch does not blank the grid mid-work. Different partner: clear
        // both, or one partner's configs — and the tab subtitle's `pagination.total` — render
        // briefly as another's.
        configs: isSamePartner ? state.configs : [],
        pagination: isSamePartner ? state.pagination : {},
        loadedPartnerId: isSamePartner ? state.loadedPartnerId : null,
        listLoading: true,
        listError: null,
      }
    }
    case AdminFetchRetryConfigs.type.SUCCESS: {
      const page = action.payload as RetryConfigsPage | undefined

      return {
        ...state,
        configs: page?.data ?? [],
        pagination: page?.pagination ?? {},
        loadedPartnerId: state.filters.partnerId,
        listLoading: false,
        listError: null,
      }
    }
    case AdminFetchRetryConfigs.type.FAILED:
      return {
        ...state,
        listLoading: false,
        listError: resolveErrorMessage(action.payload, 'Failed to load scheduled actions'),
      }

    case AdminFetchRetryConfig.type.REQUEST: {
      const payload = action.payload as FetchRetryConfigPayload | undefined
      const configId = payload?.configId ?? null
      const isSameConfig = configId === state.detailConfigId

      return {
        ...state,
        detailConfigId: configId,
        detail: isSameConfig ? state.detail : getEmptyDetail(),
        detailLoading: true,
        detailError: null,
      }
    }
    case AdminFetchRetryConfig.type.SUCCESS: {
      const detail = action.payload as RetryConfigDetail | undefined

      return {
        ...state,
        detail: detail ?? getEmptyDetail(),
        detailLoading: false,
        detailError: null,
      }
    }
    case AdminFetchRetryConfig.type.FAILED:
      return {
        ...state,
        detailLoading: false,
        detailError: resolveErrorMessage(action.payload, 'Failed to load the scheduled action'),
      }

    // Neither mutation writes into `detail` or `configs` — the saga refetches on success, which
    // is the single source of truth.
    case AdminUpdateRetryConfig.type.REQUEST:
      return {
        ...state,
        saving: true,
      }
    case AdminUpdateRetryConfig.type.SUCCESS:
    case AdminUpdateRetryConfig.type.FAILED:
      return {
        ...state,
        saving: false,
      }

    case AdminRescheduleRetryJob.type.REQUEST: {
      const payload = action.payload as RescheduleRetryJobPayload | undefined

      return {
        ...state,
        reschedulingJobId: payload?.retryJobId ?? null,
      }
    }
    case AdminRescheduleRetryJob.type.SUCCESS:
    case AdminRescheduleRetryJob.type.FAILED:
      return {
        ...state,
        reschedulingJobId: null,
      }

    default:
      return state
  }
}
